import { useState, FormEvent, useEffect, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store';
import { register } from '@/store/slices/authSlice';
import AuthSingleCardLayout from '@/components/auth/AuthSingleCardLayout';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import { AuthInlineProductPills } from '@/components/auth/LoginProductPills';
import { FiMail, FiLock, FiUser, FiAlertCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { getApiError } from '@/utils/errorUtils';
import { getPostLoginRoute } from '@/utils/productAccess';
import { persistEntryProductFromUrl, readEntryProductId } from '@/utils/entryProduct';
import { PRODUCT_BY_ID } from '@/config/products';

const setupStats = [
  { label: 'Org setup', value: '~4 min' },
  { label: 'Included now', value: 'Plan' },
  { label: 'Suite', value: '4 tools' },
];

const launchSteps = [
  'Create your organization on AIDLC — one login for every CoTaskr product.',
  'CoTaskr Plan is active immediately: projects, sprints, time, and economics.',
    'Add Code, Ship, and Lenc from billing when your team is ready — flat pricing, no tiers.',
];

export default function Register() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const entryProductId = useMemo(
    () => readEntryProductId(searchParams.toString()),
    [searchParams],
  );
  const entryProduct = PRODUCT_BY_ID[entryProductId];

  useEffect(() => {
    persistEntryProductFromUrl(searchParams.toString());
  }, [searchParams]);
  const { loading, error, isAuthenticated, user } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    organizationName: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isAuthenticated) {
      navigate(
        getPostLoginRoute(user?.tenant?.settings as Record<string, unknown> | undefined, user?.role, entryProductId),
        { replace: true },
      );
    }
  }, [isAuthenticated, navigate, user?.role, user?.tenant?.settings]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.organizationName.trim()) errors.organizationName = 'Organization name is required';
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';

    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await dispatch(
        register({
          organizationName: formData.organizationName,
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
        })
      ).unwrap();
      toast.success('Account created successfully!');
      navigate(getPostLoginRoute(undefined, 'ADMIN'), { replace: true });
    } catch (err: unknown) {
      toast.error(getApiError(err, 'Registration failed'));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
    }
  };

  return (
    <AuthSingleCardLayout
      badge="Join AIDLC"
      title={entryProductId !== 'pm' ? `Start with ${entryProduct.name}` : 'Create your organization'}
      subtitle={
        entryProductId !== 'pm'
          ? `Create your org and open ${entryProduct.shortName} — add other CoTaskr products anytime from billing.`
          : 'Set up your org on the Automated Intelligent Delivery Lifecycle Control platform. CoTaskr Plan is included from day one.'
      }
      containerWidthClass="max-w-xl"
      hideProductPills
      entryProductId={entryProductId}
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            What you get on signup
          </div>

          <div className="mt-3 grid grid-cols-3 gap-2">
            {setupStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border border-white bg-white px-2 py-3 text-center shadow-sm"
              >
                <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  {stat.label}
                </div>
                <div className="mt-1 text-sm font-black text-slate-900">{stat.value}</div>
              </div>
            ))}
          </div>

          <div className="mt-3">
            <AuthInlineProductPills />
          </div>

          <div className="mt-3 space-y-1.5">
            {launchSteps.map((step, index) => (
              <div
                key={step}
                className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm"
              >
                <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                  {index + 1}
                </div>
                <div className="pt-0.5 text-xs leading-4.5 text-slate-700">{step}</div>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="flex items-start rounded-2xl border border-red-200 bg-red-50 p-4">
            <FiAlertCircle className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Input
            id="organizationName"
            name="organizationName"
            label="Organization"
            placeholder="Acme Inc"
            value={formData.organizationName}
            onChange={handleChange}
            error={validationErrors.organizationName}
            fullWidth
            required
          />
          <div />

          <Input
            id="firstName"
            name="firstName"
            label="First name"
            placeholder="Jane"
            value={formData.firstName}
            onChange={handleChange}
            error={validationErrors.firstName}
            leftIcon={<FiUser className="w-5 h-5" />}
            fullWidth
            required
          />
          <Input
            id="lastName"
            name="lastName"
            label="Last name"
            placeholder="Doe"
            value={formData.lastName}
            onChange={handleChange}
            error={validationErrors.lastName}
            fullWidth
            required
          />
        </div>

        <Input
          id="email"
          name="email"
          type="email"
          label="Email address"
          placeholder="you@example.com"
          value={formData.email}
          onChange={handleChange}
          error={validationErrors.email}
          leftIcon={<FiMail className="w-5 h-5" />}
          fullWidth
          required
          autoComplete="email"
        />

        <Input
          id="password"
          name="password"
          type="password"
          label="Password"
          placeholder="••••••••"
          value={formData.password}
          onChange={handleChange}
          error={validationErrors.password}
          leftIcon={<FiLock className="w-5 h-5" />}
          fullWidth
          required
        />

        <Input
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          label="Confirm password"
          placeholder="••••••••"
          value={formData.confirmPassword}
          onChange={handleChange}
          error={validationErrors.confirmPassword}
          leftIcon={<FiLock className="w-5 h-5" />}
          fullWidth
          required
        />

        <Button type="submit" variant="primary" size="lg" fullWidth isLoading={loading} className="auth-submit-btn">
          Create organization
        </Button>

        <div className="text-center">
          <p className="text-sm text-slate-600">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-brand hover:text-brand-dark transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </form>
    </AuthSingleCardLayout>
  );
}
